import React, { useEffect, useState } from "react";
import { Stack, TextInput, NumberInput, Text, Paper } from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { AR_FORM_NUMBER_INPUT_PROPS } from "../helpers/arFormUtils";
import { formatFundAmount } from "../helpers/approvalFundListFundFields";
import { useServerApi } from "../hooks/userServerApi";

const CONTRACT_NO_LABEL = (
  <>
    Contract No. 合約編號
    <br />
    ( Input By Internal Only )
  </>
);

const isBackofficeUserType = (userType) =>
  String(userType ?? "").toUpperCase() === "BACKOFFICE";

const OrderArFormSubscribedAmount = ({ form }) => {
  const auth = useAuthUser();
  const authUser = auth?.() ?? {};
  const [api] = useServerApi();
  const [userType, setUserType] = useState(authUser.userType);
  const isBackoffice = isBackofficeUserType(userType);

  useEffect(() => {
    if (authUser.userType) {
      setUserType(authUser.userType);
      return;
    }
    const id = authUser.id || authUser._id;
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const user = await api.getById({ apiEntity: "user", id });
        if (!cancelled) setUserType(user?.userType);
      } catch (error) {
        console.log(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // api is recreated each render; fetch only when the signed-in user changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser.id, authUser._id, authUser.userType]);

  const {
    fundName,
    fundCurrency,
    subscribedAmount,
    contractNo,
    minSubscriptionAmount,
  } = form?.values ?? {};

  if (!String(fundName ?? "").trim()) {
    return (
      <Text size="sm" color="dimmed">
        Please select a fund in the previous step.
      </Text>
    );
  }

  const amountError = form.errors?.subscribedAmount;
  const minimumLabel =
    minSubscriptionAmount != null
      ? `${formatFundAmount(minSubscriptionAmount)} ${fundCurrency ?? ""}`.trim()
      : null;

  return (
    <Stack spacing="lg" maw={520}>
      <Paper withBorder p="md" radius="sm">
        <Text size="xs" color="dimmed" mb={4}>
          Selected fund 已選基金
        </Text>
        <Text size="sm" weight={600}>
          {fundName}
        </Text>
      </Paper>

      <TextInput
        label="Fund Currency 基金貨幣"
        value={fundCurrency ?? ""}
        readOnly
        variant="filled"
        size="md"
      />

      <NumberInput
        label="Subscribed/ Switch-in Amount 認購/轉入金額"
        required
        withAsterisk
        hideControls
        min={0}
        precision={2}
        placeholder="0.00"
        error={amountError}
        value={subscribedAmount ?? ""}
        size="md"
        onChange={(value) => {
          form.markUserEdited?.();
          form.setFieldValue("subscribedAmount", value);
          form.setFieldValue(
            "productSubscribed",
            value != null && value !== "" && Number(value) > 0
          );
        }}
        {...AR_FORM_NUMBER_INPUT_PROPS}
      />

      <TextInput
        label={CONTRACT_NO_LABEL}
        value={contractNo ?? ""}
        size="md"
        disabled={!isBackoffice}
        variant={isBackoffice ? "default" : "filled"}
        onChange={(event) => {
          if (!isBackoffice) return;
          form.markUserEdited?.();
          form.setFieldValue("contractNo", event.currentTarget.value);
        }}
      />

      {minimumLabel && (
        <Text size="sm" color="dimmed">
          Minimum subscribed/ switch-in amount 最低認購/轉入金額: {minimumLabel}
        </Text>
      )}
    </Stack>
  );
};

export default OrderArFormSubscribedAmount;
